// The record's summary (MAJLIS-153; D-60, UI-C41, UI-C42, P-40; summary-mockup.html frames (b) and (c)). Opened by the
// host's Summary icon (UI-C40) over the record, which stays mounted underneath so Back returns to it where it was.
//
// UI-C41: the record's header shape (Back, "Summary" over the day, the reading chip), then on the surface a HEADER
// block taken from the RECORD itself (day, start time and length; the people as seat dots and names in their seat
// colours; the languages spoken) - drawn at once, before the server answers - and the six sections, each split by
// the sheet's 1 px rule and titled in its muted 12 px. A section the summary left empty is not drawn; Quick recap
// always is. A notable moment is a link in the app's text-link style; a tap opens the record scrolled to that phrase,
// marked as a search's current phrase. Then "Save as file" (MAJLIS-154; ruling 4, 2026-10-02): the share sheet's
// button.secondary, under a written summary only; it makes one script-free HTML file ON THE PHONE from what is shown
// (record/summaryFile.ts) and hands it to the system share sheet where it takes files, otherwise downloads it. A
// cancelled share is not an error; a real failure is a visible line. Last, the "Generated by Majlis" line, under every
// summary shown.
//
// UI-C42: while P-40 answers 202 the header block shows with the translation slot's three breathing dots and the
// writing lines (a polite live region); the same POST is sent again after each Retry-After on the INJECTED clock, and
// the screen gives up SUMMARY_GIVE_UP_MS (ASSUMPTION, 120 s) after its first POST with the record screen's own
// failure style ("Try again"). A 503 is that failure at once; 409 too_short is its own line with no retry. Leaving
// the screen stops asking but does not cancel the server's job; coming back POSTs again. Reduced motion: the global
// rule stills the dots, and the words carry the state.
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { Clock, TimerId } from "../clock";
import { SUMMARY_GIVE_UP_MS } from "../constants";
import { languageName } from "../data/languages";
import { READ_LANGUAGES } from "../data/readLanguages";
import { dirOf, useI18n } from "../i18n";
import { shown } from "../i18n/pending";
import type { RecordData, Summary, SummaryAnswer } from "../protocol/records";
import {
  colourOf,
  nameColour,
  people,
  recordStart,
  saveFile,
  spokenLangs,
  summaryFileHtml,
  summaryFileName,
  SUMMARY_FILE_TYPE,
  type FileSaver,
} from "../record/summaryFile";
import { BackIcon, SaveIcon } from "./icons";

type State = { kind: "writing" } | { kind: "ready"; summary: Summary } | { kind: "failed" } | { kind: "too_short" };

export function SummaryScreen({
  record,
  readLang,
  clock,
  ask,
  onBack,
  onChip,
  onMoment,
  saver,
}: {
  record: RecordData;
  readLang: string;
  clock: Clock;
  /** MAJLIS-154: the phone's share sheet or download for "Save as file" */
  saver: FileSaver;
  /** one P-40 POST for this record in `lang` */
  ask: (lang: string) => Promise<SummaryAnswer>;
  onBack: () => void;
  /** the reading chip: the app's language picker (a new language POSTs for that language) */
  onChip: () => void;
  onMoment: (segmentId: string) => void;
}) {
  const { lang, t } = useI18n();
  const [attempt, setAttempt] = useState(0);
  // the written summary of THIS language and attempt, once it is on screen: its `record.people` names the header
  const key = `${readLang}:${attempt}`;
  const [ready, setReady] = useState<{ key: string; summary: Summary } | null>(null);
  const shown = ready && ready.key === key ? ready.summary : null;
  const back = useRef<HTMLButtonElement>(null);
  // the record's parts beneath are hidden while this shows: focus comes here
  useEffect(() => back.current?.focus(), []);

  const at = recordStart(record);
  const day = new Intl.DateTimeFormat(lang, { weekday: "short", month: "short", day: "numeric" }).format(at);
  const time = (ms: number) => new Intl.DateTimeFormat(lang, { hour: "2-digit", minute: "2-digit" }).format(ms);
  const readName = languageName(readLang, lang);
  const readNative = READ_LANGUAGES.find((l) => l.tag === readLang)?.native ?? readName;

  return (
    <div className="summary" data-spec="UI-C41 D-60 P-40">
      <header className="room-header record-header" data-spec="UI-C41 UI-S3">
        <button ref={back} type="button" className="icon-button" aria-label={t("back")} onClick={onBack}>
          <BackIcon />
        </button>
        <div className="room-id record-title">
          <h1 className="record-day summary-title">{t("summary")}</h1>
          <span className="record-meta">{day}</span>
        </div>
        <button
          type="button"
          className="reading-chip"
          aria-label={t("readingChip", { lang: readName })}
          onClick={onChip}
          data-spec="UI-C9 UI-C41 D-60"
        >
          <span lang={readLang} dir={dirOf(readLang)}>
            {readNative}
          </span>
        </button>
      </header>
      <div className="sum-body" data-spec="UI-C41">
        <RecordHead
          record={record}
          summary={shown}
          line={`${day} · ${time(at)} · ${t("summaryMinutes", { min: record.minutes })}`}
        />
        {/* keyed by language and attempt: a new language, or Try again, starts afresh from "writing" */}
        <Body
          key={key}
          onReady={(summary) => setReady({ key, summary })}
          record={record}
          readLang={readLang}
          clock={clock}
          ask={ask}
          time={time}
          onRetry={() => setAttempt((n) => n + 1)}
          onMoment={onMoment}
          saver={saver}
        />
      </div>
    </div>
  );
}

function RecordHead({ record, summary, line }: { record: RecordData; summary: Summary | null; line: string }) {
  const { lang, t } = useI18n();
  const list = people(record, summary);
  const langs = spokenLangs(record);
  return (
    <section className="sum-head" data-spec="UI-C41 P-21 DM-2">
      <p className="sum-when">{line}</p>
      <div className="sum-people" role="group" aria-label={t("summaryPeople")}>
        <span className="record-dots" aria-hidden="true">
          {list.map((p) => (
            <span key={p.key} className={`record-dot seat-c${p.colour}`} lang={p.lang || undefined}>
              {Array.from(p.name.trim())[0] ?? ""}
            </span>
          ))}
        </span>
        <span className="sum-names">
          {list.map((p, i) => (
            <span key={p.key}>
              {i > 0 && <span aria-hidden="true">{t("nameSep")}</span>}
              <span className={`who seat-c${p.colour}`} dir="auto">
                {p.name}
              </span>
            </span>
          ))}
        </span>
      </div>
      <p className="sum-langs" aria-label={t("summaryLangs")}>
        {langs.map((l) => languageName(l, lang)).join(" · ")}
      </p>
    </section>
  );
}

function Body({
  record,
  readLang,
  clock,
  ask,
  time,
  onRetry,
  onMoment,
  onReady,
  saver,
}: {
  saver: FileSaver;
  record: RecordData;
  readLang: string;
  clock: Clock;
  ask: (lang: string) => Promise<SummaryAnswer>;
  time: (ms: number) => string;
  onRetry: () => void;
  /** the written summary is on screen */
  onReady: (summary: Summary) => void;
  onMoment: (segmentId: string) => void;
}) {
  const { t } = useI18n();
  const [state, setState] = useState<State>({ kind: "writing" });
  const ready = useRef(onReady);
  useEffect(() => {
    ready.current = onReady;
  }, [onReady]);

  // UI-C42: POST; on 202 POST the same again after Retry-After, until an answer or the give-up. Every wait is on the
  // injected clock. Unmounting (Back, a moment, another language) stops asking; the server's job goes on.
  useEffect(() => {
    let alive = true;
    let timer: TimerId | null = null;
    const giveUp = clock.setTimeout(() => {
      alive = false;
      if (timer !== null) clock.clearTimeout(timer);
      setState({ kind: "failed" });
    }, SUMMARY_GIVE_UP_MS);
    const once = async () => {
      const a = await ask(readLang);
      if (!alive) return;
      if (a.kind === "writing") {
        timer = clock.setTimeout(() => void once(), a.retryAfterMs);
        return;
      }
      clock.clearTimeout(giveUp);
      setState(a.kind === "ready" ? { kind: "ready", summary: a.summary } : { kind: a.kind });
      if (a.kind === "ready") ready.current(a.summary);
    };
    void once();
    return () => {
      alive = false;
      clock.clearTimeout(giveUp);
      if (timer !== null) clock.clearTimeout(timer);
    };
  }, [ask, readLang, clock]);

  if (state.kind === "writing") {
    return (
      <div className="sum-loading" role="status" aria-live="polite" data-spec="UI-C42 P-40">
        <span className="translation-slot" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>
        <p className="sum-loading-line">{t("summaryWriting")}</p>
        <p className="sum-loading-hint">{t("summaryWritingHint")}</p>
      </div>
    );
  }
  if (state.kind === "failed") {
    const failedBody = shown("summaryFailedBody");
    return (
      // MAJLIS-170 (component-rules.md rules 1 and 2; the screen-rules mock-up, frame (c)): the failure in the title size,
      // one muted line under it (summaryFailedBody, once its verdict is in: i18n/pending.ts), and Try again as the one
      // filled button - retrying is the one thing to do here
      <div className="sum-loading sum-failed record-failed" role="alert" data-spec="UI-C42 P-40">
        <p className="ending-title">{t("summaryFailed")}</p>
        {failedBody !== null && <p className="ending-body">{t(failedBody)}</p>}
        <button type="button" className="primary" onClick={onRetry}>
          {t("tryAgain")}
        </button>
      </div>
    );
  }
  if (state.kind === "too_short") {
    return (
      <div className="sum-loading" role="status" data-spec="UI-C42 P-40">
        <p className="sum-too-short">{t("summaryTooShort")}</p>
      </div>
    );
  }
  return (
    <Sections
      record={record}
      summary={state.summary}
      readLang={readLang}
      time={time}
      onMoment={onMoment}
      saver={saver}
    />
  );
}

function Sections({
  record,
  summary,
  readLang,
  time,
  onMoment,
  saver,
}: {
  record: RecordData;
  summary: Summary;
  readLang: string;
  time: (ms: number) => string;
  onMoment: (segmentId: string) => void;
  saver: FileSaver;
}) {
  const { lang, t } = useI18n();
  const s = summary.sections;
  const dir = dirOf(readLang);
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  // MAJLIS-154: the file is made here, in the tap itself, and handed on with no await before it (the share sheet needs
  // the tap's activation)
  const save = () => {
    setSaveFailed(false);
    let file: File;
    try {
      const html = summaryFileHtml({ record, summary, readLang, ui: { lang, t } });
      file = new File([html], summaryFileName(record, readLang), { type: SUMMARY_FILE_TYPE });
    } catch {
      setSaveFailed(true);
      return;
    }
    setSaving(true);
    void saveFile(saver, file).then((outcome) => {
      setSaving(false);
      if (outcome === "failed") setSaveFailed(true);
    });
  };
  const seatOfPhrase = (id: string) => record.phrases.find((p) => p.segment.id === id)?.segment.seat ?? null;
  const items = (list: string[], numbered: boolean) => {
    const Tag = numbered ? "ol" : "ul";
    return (
      <Tag className="sum-list" lang={readLang} dir={dir}>
        {list.map((x, i) => (
          <li key={i}>{x}</li>
        ))}
      </Tag>
    );
  };
  return (
    <>
      <Section title={t("summaryRecap")} name="recap">
        {items(s.recap, true)}
      </Section>
      {s.topics.length > 0 && (
        <Section title={t("summaryTopics")} name="topics">
          {items(s.topics, true)}
        </Section>
      )}
      {s.decisions.length > 0 && (
        <Section title={t("summaryDecisions")} name="decisions">
          {items(s.decisions, false)}
        </Section>
      )}
      {s.actions.length > 0 && (
        <Section title={t("summaryActions")} name="actions">
          <ul className="sum-actions" lang={readLang} dir={dir}>
            {s.actions.map((a, i) => {
              const c = a.who ? nameColour(record, a.who) : null;
              return (
                <li key={i} className="action">
                  {a.who && (
                    <span className={c === null ? "who" : `who seat-c${c}`} dir="auto">
                      {a.who}
                    </span>
                  )}
                  <span className="what">{a.what}</span>
                </li>
              );
            })}
          </ul>
        </Section>
      )}
      {s.questions.length > 0 && (
        <Section title={t("summaryQuestions")} name="questions">
          {items(s.questions, false)}
        </Section>
      )}
      {s.moments.length > 0 && (
        <Section title={t("summaryMoments")} name="moments">
          <ul className="sum-moments">
            {s.moments.map((m) => {
              const seat = seatOfPhrase(m.segment_id);
              const c = seat === null ? null : colourOf(record, seat);
              const when = time(Date.parse(m.at));
              return (
                <li key={m.segment_id}>
                  <button
                    type="button"
                    className="moment"
                    data-moment={m.segment_id}
                    aria-label={t("summaryMoment", { name: m.name, time: when })}
                    onClick={() => onMoment(m.segment_id)}
                    data-spec="UI-C41 UI-S3 DM-3 DM-4"
                  >
                    <span className="moment-meta">
                      <span className={c === null ? "who" : `who seat-c${c}`} dir="auto">
                        {m.name}
                      </span>
                      <time className="when">{when}</time>
                    </span>
                    <q className="moment-q" lang={readLang} dir={dir}>
                      {m.text}
                    </q>
                  </button>
                </li>
              );
            })}
          </ul>
        </Section>
      )}
      <div className="sum-foot" data-spec="UI-C41 D-60">
        <button
          type="button"
          className="secondary sum-save"
          disabled={saving}
          onClick={save}
          data-spec="UI-C41 UI-C15 D-60"
        >
          <SaveIcon />
          {t("summarySave")}
        </button>
        {saveFailed && (
          <p className="sheet-failed sum-save-failed" role="alert">
            {t("summarySaveFailed")}
          </p>
        )}
        <p className="sum-note" data-spec="UI-C41 D-60">
          {t("summaryGenerated")}
        </p>
      </div>
    </>
  );
}

function Section({ title, name, children }: { title: string; name: string; children: ReactNode }) {
  return (
    <section className="sum-sec" data-section={name} data-spec="UI-C41 EM-35">
      <h2 className="sum-title">{title}</h2>
      {children}
    </section>
  );
}
